import { InfoCard } from '#app/components/ui/info-card.tsx'
import { type Route } from './+types/scholarships.ts'

type QuickLinkSection = {
	id: string
	label: string
	cards: Array<{ id: string; label: string }>
}

const QUICK_LINK_SECTIONS: QuickLinkSection[] = [
	{
		id: 'general-information',
		label: 'General Information',
		cards: [{ id: 'general-info-overview', label: 'Scholarship Overview' }],
	},
	{
		id: 'using-scholarship',
		label: 'Using Your Scholarship',
		cards: [
			{
				id: 'important-registration-details',
				label: 'Important Registration Details',
			},
		],
	},
	{
		id: 'general-scholarships',
		label: 'General Scholarships',
		cards: [
			{ id: 'nationals-scholarships', label: 'Nationals Scholarships' },
			{ id: 'courage-scholarships', label: 'Courage Scholarships' },
			{ id: 'giving-keys-scholarships', label: 'Giving Keys Scholarships' },
		],
	},
	{
		id: 'audition-scholarships',
		label: 'Audition Scholarships',
		cards: [
			{
				id: 'audition-overview',
				label: 'Audition Class Overview',
			},
			{
				id: 'junior-teen-audition-process',
				label: 'Junior & Teen Audition Process',
			},
			{ id: 'senior-audition-process', label: 'Senior Audition Process' },
			{
				id: 'audition-scholarship-awards',
				label: 'Audition Scholarship Awards',
			},
		],
	},
	{
		id: 'the-collective',
		label: 'The Collective',
		cards: [
			{ id: 'collective-overview', label: 'Program Overview' },
			{ id: 'collective-program', label: 'The Collective' },
			{ id: 'collective-jv-program', label: 'The Collective JV' },
		],
	},
	{
		id: 'scholarship-questions',
		label: 'Questions About Scholarships?',
		cards: [],
	},
]

function QuickLinksPanel() {
	return (
		<div className="border border-muted-foreground/30 bg-muted/30 p-4 sm:p-6">
			<div className="mb-4 flex items-center gap-3 sm:mb-5">
				<div className="flex h-8 w-8 items-center justify-center text-primary">
					<svg
						className="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
						/>
					</svg>
				</div>
				<h3 className="font-display text-base font-bold tracking-wide sm:text-lg">
					QUICK LINKS
				</h3>
			</div>

			<div className="space-y-3">
				{QUICK_LINK_SECTIONS.map((section, sectionIndex) => (
					<div
						key={section.id}
						className="border border-border/50 bg-background/95 p-3"
					>
						<div className="mb-2 flex items-center gap-2">
							<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-foreground">
								{sectionIndex + 1}
							</span>
							<a
								href={`#${section.id}`}
								className="inline-flex flex-1 items-center border border-border/60 bg-muted/40 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground transition-colors hover:border-primary/50 hover:text-primary"
							>
								<span>{section.label}</span>
							</a>
						</div>

						{section.cards.length > 0 ? (
							<div className="ml-2 space-y-1.5 border-l border-border/50 pl-3">
								{section.cards.map((card) => (
									<a
										key={card.id}
										href={`#${card.id}`}
										className="group flex items-start gap-2 rounded-sm border border-transparent px-2 py-1.5 text-xs leading-snug text-foreground/85 transition-all hover:border-border/60 hover:bg-muted/40 hover:text-foreground"
									>
										<span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/60 transition-colors group-hover:bg-primary" />
										<span className="flex-1">{card.label}</span>
									</a>
								))}
							</div>
						) : (
							<p className="ml-2 text-xs text-muted-foreground">
								General section details.
							</p>
						)}
					</div>
				))}
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Scholarships | DanceMakers' },
	{
		name: 'description',
		content:
			'Learn about DanceMakers scholarship opportunities including Nationals scholarships, Courage scholarships, audition scholarships, and The Collective program.',
	},
]

export default function ScholarshipsInfoRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
						2026-27 Season
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Scholarship Information
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						DanceMakers is committed to recognizing outstanding dancers through
						various scholarship opportunities. From convention scholarships to
						The Collective program, we celebrate talent, dedication, and
						courage.
					</p>
				</div>
			</section>

			<div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-8 lg:px-6 xl:px-12">
				<div className="min-w-0">
					{/* General Information */}
					<section
						id="general-information"
						className="scroll-mt-36 bg-muted/30 py-8 md:py-12"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								General Information
							</h2>

							<div
								id="general-info-overview"
								className="max-w-4xl scroll-mt-36 space-y-4 text-xs leading-relaxed text-muted-foreground sm:space-y-6 sm:text-sm"
							>
								<p>
									Scholarships will be awarded to dancers for various reasons
									including, but not limited to:{' '}
									<span className="font-medium text-foreground">
										class performance, excelling at a particular genre, focus,
										attitude, talent, drive, sportsmanship
									</span>
									, etc.
								</p>
								<p>
									DanceMakers aims to ensure students are recognized no matter
									where they are in the room and to{' '}
									<span className="font-medium text-foreground">
										recognize the dancers by name, not by number
									</span>
									. We have designed our scholarship tags to highlight their
									name large and their number small in the upper corner. This is
									meant to help promote an intimate and welcoming environment.
								</p>
								<p className="font-medium text-foreground">
									Each dancer must wear a scholarship tag throughout the entire
									weekend to be eligible for scholarships.
								</p>
							</div>
						</div>
					</section>

					{/* Scholarship Registration */}
					<section
						id="using-scholarship"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Using Your Scholarship
							</h2>

							<div
								id="important-registration-details"
								className="max-w-4xl scroll-mt-36"
							>
								<InfoCard title="Important Registration Details">
									<div className="space-y-3">
										<p>
											<span className="font-medium text-foreground">
												All scholarships are nontransferable and property of the
												recipient's studio.
											</span>{' '}
											Registrations for scholarship winners must be on the
											studio account and not independent registrations.
										</p>
										<p>
											All scholarship winner names have been flagged in our
											system. When adding the attendee to your registration,
											check the box next to the attendee's name that says
											"Scholarship" and choose the appropriate option. Your fee
											will be removed at this point.
										</p>
										<p className="font-medium text-foreground">
											Scholarships are non-transferable.
										</p>
									</div>
								</InfoCard>
							</div>
						</div>
					</section>

					{/* General Scholarships */}
					<section
						id="general-scholarships"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								General Scholarships
							</h2>
							<p className="mb-6 max-w-4xl text-xs leading-relaxed text-muted-foreground sm:mb-8 sm:text-sm">
								These scholarships are presented from all classes throughout the
								weekend to recognize outstanding dancers.
							</p>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="nationals-scholarships" className="scroll-mt-36">
									<InfoCard title="Nationals Scholarships">
										<p className="mb-3">
											These scholarships are awarded by each of our faculty
											members to outstanding dancers in the{' '}
											<span className="font-medium text-foreground">
												Camp Dance, Junior, Teen and Senior ballrooms
											</span>{' '}
											in each city.
										</p>
										<p className="mb-3">
											These scholarships give dancers the opportunity to be
											recognized in all styles of dance for their hard work and
											dedication.
										</p>
										<p className="font-medium text-foreground">
											The scholarship is valid for our Nationals event held in
											Myrtle Beach, SC June 2027.
										</p>
									</InfoCard>
								</div>

								<div id="courage-scholarships" className="scroll-mt-36">
									<InfoCard title="Courage Scholarships">
										<p className="mb-3">
											The Courage Scholarship can be awarded to dancers in any
											age level. We are aware that following your passion and
											going after your goals requires dedication, consistency,
											hard work, and courage!
										</p>
										<p className="mb-3">
											This is meant to give dancers affirmation that they are
											working towards something much bigger than dance and that
											they can bring this courage into all aspects of life.
										</p>
										<p className="font-medium text-foreground">
											Courage scholarships are valid for 1 regional convention
											from the time it was presented to the end of the 2027-28
											Tour.
										</p>
									</InfoCard>
								</div>

								<div
									id="giving-keys-scholarships"
									className="scroll-mt-36 md:col-span-2"
								>
									<InfoCard title="Giving Keys Scholarships">
										<div className="space-y-3">
											<p>
												This scholarship comes with a{' '}
												<span className="font-medium text-foreground">
													customized engraved necklace
												</span>{' '}
												and also comes with a{' '}
												<span className="font-medium text-foreground">
													Nationals Scholarship
												</span>
												.
											</p>
											<p className="font-medium text-foreground">
												This scholarship is all about paying it forward.
											</p>
											<p>
												We want you to wear this necklace until you come across
												someone who you feel needs the message more than you do.
												You will then give this person your necklace and tell
												them how the message has helped you in your life.
											</p>
											<p>
												It is awarded to a dancer who we feel may need this
												message at this particular point in life. It is meant to
												encourage growth and broaden perspective, and to
												acknowledge their perseverance and hard work.
											</p>
											<p className="font-medium text-foreground">
												This scholarship is valid for our Nationals event held
												in Myrtle Beach, SC June 2027.
											</p>
										</div>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Audition Scholarships */}
					<section
						id="audition-scholarships"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Audition Scholarships
							</h2>

							<div
								id="audition-overview"
								className="mb-6 max-w-4xl scroll-mt-36 space-y-4 text-xs leading-relaxed text-muted-foreground sm:mb-8 sm:space-y-6 sm:text-sm"
							>
								<p>
									The DanceMakers Audition Class is meant to be just that;{' '}
									<span className="font-medium text-foreground">a class</span>.
									While we will hold a mock audition and award scholarships and
									performance opportunities, this class is meant to teach
									dancers "how" to audition.
								</p>
								<p>
									We are aware that this is not an actual audition and should be
									meant to guide dancers on how to be prepared for an audition
									in the real world. There will be feedback given to dancers
									individually to help correct mistakes and point out what they
									did correctly.
								</p>
								<p>
									We do our best to provide a{' '}
									<span className="font-medium text-foreground">
										positive, encouraging, and loving atmosphere
									</span>{' '}
									while also preparing the dancers for what to expect in the
									real audition world.
								</p>
							</div>

							<div className="mb-6 grid max-w-6xl gap-4 sm:mb-8 sm:gap-6 md:grid-cols-2">
								<div id="junior-teen-audition-process" className="scroll-mt-36">
									<InfoCard title="Junior & Teen Audition Process">
										<p className="mb-3">
											Audition classes will be held in the{' '}
											<span className="font-medium text-foreground">
												Junior, Teen, and Senior rooms
											</span>
											. In each class, there will be a combination presented,
											which will directly be followed by the "audition process".
										</p>
										<p className="mb-3">
											In the Junior and Teen levels, all dancers will perform in
											groups, notes will be taken, and constructive criticism
											and positive reinforcement will be given. We will narrow
											the Junior and Teens down to finalists.
										</p>
										<p className="font-medium text-foreground">
											All scholarship winners of these auditions will be
											announced on Sunday at the Wrap It Ceremony.
										</p>
									</InfoCard>
								</div>

								<div id="senior-audition-process" className="scroll-mt-36">
									<InfoCard title="Senior Audition Process">
										<p className="mb-3">
											In the Senior level, the dancers will perform in groups,
											and constructive criticism will be presented while
											narrowing down the finalists to the{' '}
											<span className="font-medium text-foreground">
												top 20-30 dancers
											</span>
											, depending on the city.
										</p>
										<p className="mb-3">
											All Senior room finalists will be asked to{' '}
											<span className="font-medium text-foreground">
												perform at the Wrap It Ceremony on Sunday
											</span>
											.
										</p>
										<p>
											Directly following the performance, the scholarship
											winners will be announced.
										</p>
									</InfoCard>
								</div>
							</div>

							<div
								id="audition-scholarship-awards"
								className="max-w-4xl scroll-mt-36"
							>
								<InfoCard title="Audition Scholarship Awards">
									<p>
										<span className="font-medium text-foreground">
											4 Regional scholarships will be awarded in each audition
										</span>{' '}
										and announced during our Wrap It Ceremony for Junior, Teen &
										Senior levels. They are valid from the time it was presented
										to the end of the 2027-28 Tour.
									</p>
								</InfoCard>
							</div>
						</div>
					</section>

					{/* The Collective */}
					<section
						id="the-collective"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								The Collective
							</h2>

							<div
								id="collective-overview"
								className="mb-6 max-w-4xl scroll-mt-36 space-y-4 text-xs leading-relaxed text-muted-foreground sm:mb-8 sm:space-y-6 sm:text-sm"
							>
								<p>
									<span className="font-medium text-foreground">
										The Collective and Collective JV are elite groups of dancers
									</span>{' '}
									chosen by the DanceMakers faculty as outstanding
									representatives of "who we are".
								</p>
								<p>
									Dancers who show an{' '}
									<span className="font-medium text-foreground">
										"it" factor
									</span>{' '}
									are hand chosen at Nationals by our faculty and invited to
									attend every stop on the following tour.
								</p>
								<p>
									The criteria is based on recognizing{' '}
									<span className="font-medium text-foreground">
										technique, drive, style or attitude, and attendance
									</span>
									.
								</p>
								<p>
									The opportunities gained in being a part of this once in a
									lifetime program will add to the work experiences and
									performing opportunities for dancers who are on the road to a
									professional career.
								</p>
								<p>
									The members of "The Collective" will have the privilege of
									training, studying, and performing with our world renowned
									teachers and choreographers. This program allows outstanding
									dancers to become a part of the DanceMakers FAM and travel the
									country as assistants, performers and staff members for a well
									rounded experience in the dance industry.
								</p>
							</div>

							<div className="grid max-w-6xl gap-4 sm:gap-8 md:grid-cols-2">
								{/* The Collective */}
								<div
									id="collective-program"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-8"
								>
									<h3 className="mb-4 font-display text-lg font-bold uppercase tracking-wider text-foreground sm:mb-6 sm:text-xl">
										The Collective
									</h3>
									<div className="space-y-3 text-xs leading-relaxed text-muted-foreground sm:space-y-4 sm:text-sm">
										<div>
											<p className="mb-2 font-medium text-primary">
												Convention Scholarships
											</p>
											<p>
												A year scholarship to all DanceMakers Conventions for
												their reigning year, as well as, for Collective only a{' '}
												<span className="font-bold text-foreground">
													lifetime scholarship to DanceMakers conventions!
												</span>
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Recognition
											</p>
											<p>
												Honored at all Awards Ceremonies, such as the Warm Up
												and Wrap It.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">Apparel</p>
											<p>
												Receive complimentary DanceMakers apparel and of course,
												the iconic{' '}
												<span className="font-medium text-foreground">
													"The Collective" letterman jacket
												</span>
												.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Collective Weekend
											</p>
											<p>
												A free trip to our "Collective Weekend," where members
												will receive head-shots, a welcome dinner, choreography
												for the upcoming season's performance and private
												classes.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Travel & Accommodations
											</p>
											<p>
												Travel arrangements and/or accommodations for "The
												Collective" for at least{' '}
												<span className="font-bold text-foreground">
													SEVEN cities
												</span>{' '}
												per dancer.{' '}
												<span className="font-medium text-foreground">
													This makes it the first and only program in the
													industry providing financial support to mentorship
													programs.
												</span>
											</p>
										</div>
									</div>
								</div>

								{/* The Collective JV */}
								<div
									id="collective-jv-program"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-8"
								>
									<h3 className="mb-4 font-display text-lg font-bold uppercase tracking-wider text-foreground sm:mb-6 sm:text-xl">
										The Collective JV
									</h3>
									<div className="space-y-3 text-xs leading-relaxed text-muted-foreground sm:space-y-4 sm:text-sm">
										<div>
											<p className="mb-2 font-medium text-primary">
												Convention Scholarships
											</p>
											<p>
												A year long scholarship to all DanceMakers Conventions
												for their reigning year, as well as a{' '}
												<span className="font-medium text-foreground">
													year long scholarship for the following season
												</span>
												.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Recognition
											</p>
											<p>
												Honored at all Awards Ceremonies, such as the Warm Up
												and Wrap It.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">Apparel</p>
											<p>
												Receive complimentary DanceMakers apparel and of course,
												the iconic{' '}
												<span className="font-medium text-foreground">
													"The Collective JV" letterman jacket
												</span>
												.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Collective Weekend
											</p>
											<p>
												A free trip to our "Collective Weekend," where members
												will receive head-shots, a welcome dinner, choreography
												for the upcoming season's performance and private
												classes.
											</p>
										</div>

										<div>
											<p className="mb-2 font-medium text-primary">
												Travel & Accommodations
											</p>
											<p>
												Travel arrangements and/or accommodations for "The
												Collective JV" for at least{' '}
												<span className="font-bold text-foreground">
													FIVE cities
												</span>{' '}
												per dancer.{' '}
												<span className="font-medium text-foreground">
													This makes it the first and only program in the
													industry providing financial support to mentorship
													programs.
												</span>
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>

					{/* Contact CTA */}
					<section
						id="scholarship-questions"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 text-center md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
								Questions About Scholarships?
							</h2>
							<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
								Have questions about scholarship opportunities or The Collective
								program? Our team is here to help!
							</p>
							<a
								href="/contact"
								className="inline-block border border-primary px-8 py-3 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary/90"
							>
								Contact Us
							</a>
						</div>
					</section>
				</div>

				<aside className="hidden lg:sticky lg:top-32 lg:block lg:self-start">
					<div className="max-h-[calc(100vh-9rem)] overflow-y-auto pr-1">
						<QuickLinksPanel />
					</div>
				</aside>
			</div>
		</div>
	)
}
